import React, { useState, useEffect, useCallback } from "react";
import { useBackendStatus, POLL_OFFLINE_SECONDS } from "../context/BackendStatusContext";
import styles from "./ServerDownPage.module.css";

/**
 * Ekran przerwy technicznej — pełnoekranowy fallback, którym down-detektor zastępuje CAŁY UI,
 * gdy backend nie odpowiada. Uczciwa maszyna stanów:
 *   • checking   — trwa weryfikacja /health (po sygnale z axios/SignalR albo z pollu),
 *   • down       — potwierdzona niedostępność, odliczanie do kolejnej próby,
 *   • recovered  — backend wrócił, za chwilę twardy reload do ekranu logowania.
 */
const ServerDownPage = () => {
    const { lastChecked, isVerifying, justRecovered, checkNow } = useBackendStatus();
    const [countdown, setCountdown] = useState(POLL_OFFLINE_SECONDS);
    const [manualCheck, setManualCheck] = useState(false);

    // Faza niesie NAGŁÓWEK i kolor — celowo tylko dwa stabilne stany, żeby tytuł nie migał.
    // „Sprawdzanie" to nie osobna faza (poll leci co kilka sekund) — żyje w pigułce statusu niżej.
    const phase = justRecovered ? "recovered" : "down";
    const verifying = isVerifying || manualCheck;

    // Odliczanie do następnej automatycznej próby (reset po każdym sprawdzeniu).
    useEffect(() => {
        if (phase === "recovered") return;
        setCountdown(POLL_OFFLINE_SECONDS);
        const timer = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? POLL_OFFLINE_SECONDS : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [lastChecked, phase]);

    const handleCheckNow = useCallback(async () => {
        if (manualCheck) return;
        setManualCheck(true);
        try {
            await checkNow();
        } finally {
            setManualCheck(false);
            setCountdown(POLL_OFFLINE_SECONDS);
        }
    }, [checkNow, manualCheck]);

    const formatTime = (date) =>
        date ? date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

    const copy = {
        recovered: {
            title: "Połączenie przywrócone",
            sub: <>Serwer znów odpowiada. Za chwilę przeniesiemy Cię do ekranu logowania…</>,
        },
        down: {
            title: "Przerwa techniczna",
            sub: <>Serwer aplikacji jest tymczasowo niedostępny.<br />Trwa przywracanie usługi.</>,
        },
    }[phase];

    return (
        <div className={`${styles.page} ${styles[`page_${phase}`]}`} role="alert" aria-live="assertive">
            <div className={styles.aurora}>
                <span className={styles.beam1} />
                <span className={styles.beam2} />
                <span className={styles.beam3} />
            </div>

            <div className={styles.card}>
                {/* Sygnał łączności — rdzeń + rozchodzące się łuki; kolor niesie stan */}
                <div className={styles.signal}>
                    <span className={styles.ring} />
                    <span className={styles.ring} />
                    <span className={styles.ring} />
                    <span className={styles.core}>
                        {phase === "recovered" ? (
                            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                                <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.4"
                                    strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                                <path d="M4 18V9M9 18V5M14 18v-6M19 18v-9" fill="none" stroke="currentColor"
                                    strokeWidth="2.2" strokeLinecap="round" />
                            </svg>
                        )}
                    </span>
                </div>

                <h1 className={styles.title}>{copy.title}</h1>
                <p className={styles.subtitle}>{copy.sub}</p>

                {phase !== "recovered" && (
                    <>
                        <div className={`${styles.statusBox} ${verifying ? styles.statusChecking : ""}`}>
                            {verifying ? <span className={styles.miniSpinner} /> : <span className={styles.statusDot} />}
                            <span className={styles.statusText}>
                                {verifying ? "Sprawdzanie połączenia…" : "Oczekiwanie na serwer"}
                            </span>
                        </div>

                        <div className={styles.checkInfo}>
                            <div className={styles.checkRow}>
                                <span className={styles.checkLabel}>Ostatnie sprawdzenie</span>
                                <span className={styles.checkValue}>{formatTime(lastChecked)}</span>
                            </div>
                            <div className={styles.checkRow}>
                                <span className={styles.checkLabel}>Następna próba za</span>
                                <span className={styles.checkValue}>
                                    <span className={styles.countdownNum}>{countdown}</span> s
                                </span>
                            </div>
                        </div>

                        <button
                            className={`${styles.checkBtn} ${manualCheck ? styles.checkBtnActive : ""}`}
                            onClick={handleCheckNow}
                            disabled={manualCheck}
                        >
                            {manualCheck ? (<><span className={styles.spinner} /> Sprawdzam…</>) : "Sprawdź teraz"}
                        </button>
                    </>
                )}

                {phase === "recovered" && (
                    <div className={styles.reloadRow}>
                        <span className={styles.spinner} /> Przenoszę do logowania…
                    </div>
                )}

                {/* Obawa użytkownika wprost: dane nie zostają na ekranie zawieszonej sesji */}
                <div className={styles.safety}>
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" className={styles.safetyIcon}>
                        <path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z" fill="none"
                            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                        <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.7"
                            strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>
                        Twoje dane są bezpieczne. Ze względów bezpieczeństwa po przywróceniu usługi
                        wymagane będzie ponowne zalogowanie.
                    </span>
                </div>

                <div className={styles.footer}>
                    <span className={styles.brand}>mPracownik</span>
                    <span className={styles.separator}>·</span>
                    <span className={styles.company}>Krosno Glass S.A.</span>
                </div>
            </div>
        </div>
    );
};

export default ServerDownPage;
